import { useEffect, useMemo, useState } from "react";

type ConnectionMode = "online" | "offline_local" | "unknown";

type SummaryCopy = {
  onlineTitle: string;
  onlineBody: string;
  offlineTitle: string;
  offlineBody: string;
  offlineVoiceInput: string;
  offlineVoiceOutput: string;
};

const API_BASE = String(
  import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_URL ||
    "https://api.avaaio.com"
).replace(/\/+$/, "");

const COPY: Record<string, SummaryCopy> = {
  en: {
    onlineTitle: "Online mode is active",
    onlineBody:
      "Ava can use connected AVAAIO services, live information, integrations, and cloud voice features.",
    offlineTitle: "Offline Local is active",
    offlineBody:
      "Typed conversations remain available. Live information, online integrations, and cloud-based voice features are turned off.",
    offlineVoiceInput:
      "Voice input is not currently available in Offline Local mode on this device.",
    offlineVoiceOutput:
      "Voice output may use your device’s built-in voice when supported.",
  },
  es: {
    onlineTitle: "El modo en línea está activo",
    onlineBody:
      "Ava puede usar los servicios conectados de AVAAIO, información en vivo, integraciones y funciones de voz en la nube.",
    offlineTitle: "El modo local sin conexión está activo",
    offlineBody:
      "Las conversaciones escritas siguen disponibles. La información en vivo, las integraciones en línea y las funciones de voz en la nube están desactivadas.",
    offlineVoiceInput:
      "La entrada de voz no está disponible actualmente en el modo local sin conexión en este dispositivo.",
    offlineVoiceOutput:
      "La salida de voz puede usar la voz integrada del dispositivo cuando sea compatible.",
  },
  fr: {
    onlineTitle: "Le mode en ligne est actif",
    onlineBody:
      "Ava peut utiliser les services AVAAIO connectés, les informations en direct, les intégrations et les fonctions vocales infonuagiques.",
    offlineTitle: "Le mode local hors ligne est actif",
    offlineBody:
      "Les conversations écrites restent disponibles. Les informations en direct, les intégrations en ligne et les fonctions vocales infonuagiques sont désactivées.",
    offlineVoiceInput:
      "La saisie vocale n’est actuellement pas disponible en mode local hors ligne sur cet appareil.",
    offlineVoiceOutput:
      "La sortie vocale peut utiliser la voix intégrée de votre appareil lorsqu’elle est prise en charge.",
  },
  de: {
    onlineTitle: "Der Online-Modus ist aktiv",
    onlineBody:
      "Ava kann verbundene AVAAIO-Dienste, Live-Informationen, Integrationen und Cloud-Sprachfunktionen verwenden.",
    offlineTitle: "Offline Lokal ist aktiv",
    offlineBody:
      "Geschriebene Unterhaltungen bleiben verfügbar. Live-Informationen, Online-Integrationen und cloudbasierte Sprachfunktionen sind ausgeschaltet.",
    offlineVoiceInput:
      "Die Spracheingabe ist im Modus Offline Lokal auf diesem Gerät derzeit nicht verfügbar.",
    offlineVoiceOutput:
      "Die Sprachausgabe kann die integrierte Gerätestimme verwenden, sofern sie unterstützt wird.",
  },
  it: {
    onlineTitle: "La modalità online è attiva",
    onlineBody:
      "Ava può utilizzare i servizi AVAAIO connessi, le informazioni in tempo reale, le integrazioni e le funzioni vocali cloud.",
    offlineTitle: "La modalità locale offline è attiva",
    offlineBody:
      "Le conversazioni scritte rimangono disponibili. Le informazioni in tempo reale, le integrazioni online e le funzioni vocali cloud sono disattivate.",
    offlineVoiceInput:
      "L’ingresso vocale non è attualmente disponibile in modalità locale offline su questo dispositivo.",
    offlineVoiceOutput:
      "L’uscita vocale può utilizzare la voce integrata del dispositivo quando supportata.",
  },
  pt: {
    onlineTitle: "O modo online está ativo",
    onlineBody:
      "A Ava pode usar serviços AVAAIO conectados, informações ao vivo, integrações e recursos de voz na nuvem.",
    offlineTitle: "O modo local offline está ativo",
    offlineBody:
      "As conversas digitadas continuam disponíveis. Informações ao vivo, integrações online e recursos de voz na nuvem estão desativados.",
    offlineVoiceInput:
      "A entrada de voz não está disponível no modo local offline neste dispositivo no momento.",
    offlineVoiceOutput:
      "A saída de voz pode usar a voz integrada do dispositivo quando houver suporte.",
  },
};

function readLanguage(): string {
  const candidates = [
    localStorage.getItem("avaaio_ui_language"),
    localStorage.getItem("preferred_language"),
    document.documentElement.lang,
    navigator.language,
  ];

  for (const candidate of candidates) {
    const language = String(candidate || "")
      .trim()
      .toLowerCase()
      .split("-")[0];

    if (COPY[language]) {
      return language;
    }
  }

  return "en";
}

function normalizeMode(payload: any): ConnectionMode {
  const nested =
    payload?.connection_mode && typeof payload.connection_mode === "object"
      ? payload.connection_mode
      : {};

  const raw = String(
    payload?.effective_mode ??
      payload?.effective ??
      payload?.resolved_mode ??
      payload?.selected_mode ??
      payload?.selected ??
      payload?.mode ??
      nested?.effective_mode ??
      nested?.effective ??
      nested?.selected_mode ??
      nested?.mode ??
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    raw === "offline" ||
    raw === "local" ||
    raw === "offline_local" ||
    raw === "local_offline"
  ) {
    return "offline_local";
  }

  if (
    raw === "online" ||
    raw === "cloud" ||
    raw === "connected"
  ) {
    return "online";
  }

  return "unknown";
}

export default function ConnectionModeAudienceSummary() {
  const [mode, setMode] = useState<ConnectionMode>("unknown");
  const language = useMemo(readLanguage, []);
  const copy = COPY[language] || COPY.en;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/v1/auth/connection-mode`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          return;
        }

        const payload = await response.json();

        if (!cancelled) {
          setMode(normalizeMode(payload));
        }
      } catch {
        // The existing Connection Mode panel remains authoritative.
      }
    };

    void load();

    const interval = window.setInterval(() => {
      void load();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  if (mode === "unknown") {
    return null;
  }

  const offline = mode === "offline_local";

  return (
    <section
      aria-live="polite"
      style={{
        marginTop: 18,
        padding: "16px 17px",
        borderRadius: 16,
        border: "1px solid rgba(121, 225, 255, 0.22)",
        background: "rgba(5, 18, 31, 0.72)",
      }}
    >
      <div
        style={{
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: "0.01em",
          color: "#f8fcff",
        }}
      >
        {offline ? copy.offlineTitle : copy.onlineTitle}
      </div>

      <p
        style={{
          margin: "8px 0 0",
          fontSize: 13,
          lineHeight: 1.55,
          color: "rgba(224, 242, 255, 0.82)",
        }}
      >
        {offline ? copy.offlineBody : copy.onlineBody}
      </p>

      {offline ? (
        <>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              lineHeight: 1.55,
              color: "rgba(224, 242, 255, 0.82)",
            }}
          >
            {copy.offlineVoiceInput}
          </p>

          <p
            style={{
              margin: "8px 0 0",
              fontSize: 13,
              lineHeight: 1.55,
              color: "rgba(224, 242, 255, 0.82)",
            }}
          >
            {copy.offlineVoiceOutput}
          </p>
        </>
      ) : null}
    </section>
  );
}
